import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { ExtraDto, ExtraSavedDto } from '@tiffinify/contracts';
import { addDays, formatDay, mealsOf, type Meal, type Slot } from '@tiffinify/core';
import { Badge, BtnSecondary, Card, CardTitle, Chip, Chips, ErrorText, Field, Helper, MealIcon, Sheet, useConfirm } from '../components/ui';
import { ApiClientError, del, post, put } from '../lib/api';
import { errorMessage, formatInr, mealName } from '../lib/format';
import { Btn, Card as PCard, Ic, Row, Seg } from '../components/p';

/**
 * Extras: more of a subscriber's tiffins on one day (one more lunch, or a dinner their lunch plan doesn't have).
 * Each is its own small bill: the plan's price, dues and refunds don't change.
 */
export function ExtrasCard({ subscriptionId, name, extras, served, today, lastDay, canAdd }: {
  subscriptionId: string;
  name: string;
  extras: ExtraDto[];
  /** Meals the kitchen serves. */
  served: Slot;
  today: string;
  /** The plan's last day: an extra goes in the plan's bag, so it can't be later. */
  lastDay: string;
  canAdd: boolean;
}) {
  const qc = useQueryClient();
  const { confirm, sheet: confirmSheet } = useConfirm();
  const [adding, setAdding] = useState(false);
  const [paying, setPaying] = useState<string | null>(null);
  const changed = () => void qc.invalidateQueries();

  const pay = useMutation({
    mutationFn: ({ id, method }: { id: string; method: 'upi' | 'cash' }) => put<ExtraDto>(`/extras/${id}/paid`, { method }),
    onSuccess: () => { setPaying(null); changed(); },
  });
  const unpay = useMutation({ mutationFn: (id: string) => del<ExtraDto>(`/extras/${id}/paid`), onSuccess: changed });
  const remove = useMutation({ mutationFn: (id: string) => del(`/extras/${id}`), onSuccess: changed });

  const unpaid = extras.filter((x) => !x.paidOn).reduce((s, x) => s + x.price, 0);
  if (extras.length === 0 && !canAdd) return null;

  return (
    <Card pad>
      <CardTitle title="Extras" right={unpaid > 0 ? <Badge kind="due">{formatInr(unpaid)} unpaid</Badge> : extras.length > 0 ? <Badge kind="ok" icon="check">Paid up</Badge> : undefined} />
      {extras.length === 0 ? (
        <Helper>One more tiffin on a day, or a meal their plan doesn’t have. Billed on its own; the plan stays as it is.</Helper>
      ) : (
        <div className="extra-list">
          {extras.map((x) => (
            <div key={x.id} className="extra-row">
              <div className="extra-main">
                <span className="extra-what"><MealIcon meal={x.meal} size={15} /><b>{mealName(x.meal)} · {formatDay(x.day)}</b>{x.quantity > 1 && <Badge>×{x.quantity}</Badge>}</span>
                {x.note && <small>“{x.note}”</small>}
                <small>{x.paidOn ? `Paid ${x.method === 'upi' ? 'by UPI' : 'in cash'} on ${formatDay(x.paidOn)}` : x.price > 0 ? 'Unpaid' : 'No charge'}</small>
              </div>
              <b className="extra-price">{x.price > 0 ? formatInr(x.price) : 'Free'}</b>
              {paying === x.id ? (
                <div className="extra-actions">
                  <BtnSecondary sm label="Cash" busy={pay.isPending && pay.variables?.method === 'cash'} onClick={() => pay.mutate({ id: x.id, method: 'cash' })} />
                  <BtnSecondary sm label="UPI" busy={pay.isPending && pay.variables?.method === 'upi'} onClick={() => pay.mutate({ id: x.id, method: 'upi' })} />
                  <button type="button" className="link" onClick={() => setPaying(null)}>Cancel</button>
                </div>
              ) : (
                <div className="extra-actions">
                  {!x.paidOn && x.price > 0 && <BtnSecondary sm ink label="Mark paid" icon="rupee" onClick={() => setPaying(x.id)} />}
                  {x.paidOn && <button type="button" className="link" disabled={unpay.isPending} onClick={() => unpay.mutate(x.id)}>Not paid</button>}
                  {x.canRemove && (
                    <button
                      type="button"
                      className="link"
                      disabled={remove.isPending}
                      onClick={async () => { if (await confirm({ title: 'Remove this extra?', body: `${mealName(x.meal)} on ${formatDay(x.day)} comes off the kitchen list.`, yes: 'Remove', danger: true })) remove.mutate(x.id); }}
                    >Remove</button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <ErrorText>{pay.error ? errorMessage(pay.error) : unpay.error ? errorMessage(unpay.error) : remove.error ? errorMessage(remove.error) : ''}</ErrorText>
      {canAdd && <BtnSecondary label="Add an extra" icon="plus" onClick={() => setAdding(true)} />}
      {adding && <AddExtra subscriptionId={subscriptionId} name={name} served={served} today={today} lastDay={lastDay} onClose={() => { setAdding(false); changed(); }} />}
      {confirmSheet}
    </Card>
  );
}

function AddExtra({ subscriptionId, name, served, today, lastDay, onClose }: { subscriptionId: string; name: string; served: Slot; today: string; lastDay: string; onClose: () => void }) {
  const meals = mealsOf(served);
  const [day, setDay] = useState(today);
  const [picking, setPicking] = useState(false);
  const [meal, setMeal] = useState<Meal>(meals.includes('lunch') ? 'lunch' : meals[0] ?? 'lunch');
  const [quantity, setQuantity] = useState(1);
  const [each, setEach] = useState('');
  const [note, setNote] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  // The price is set for one tiffin and saved for all of them.
  const total = Number(each || 0) * quantity;
  const save = useMutation({
    mutationFn: () => post<ExtraSavedDto>(`/subscriptions/${subscriptionId}/extras`, { day, meal, quantity, price: total, note: note.trim() }),
    onError: (e) => setFields(e instanceof ApiClientError ? e.fields : {}),
  });
  const first = name.split(' ')[0];

  if (save.data) {
    const x = save.data.extra;
    return (
      <Sheet title="Extra added" kicker={`${mealName(x.meal)} · ${formatDay(x.day)}`} onClose={onClose}>
        <Helper icon="check">It’s on the kitchen list and the delivery run for that day{x.price > 0 ? `, and ${formatInr(x.price)} shows as unpaid until it’s paid` : ''}.</Helper>
        <Btn label={`Tell ${first} on WhatsApp`} go="send" href={save.data.message.link} onClick={onClose} />
        <BtnSecondary label="Done" onClick={onClose} />
      </Sheet>
    );
  }

  // Today and the next two days, as long as the plan runs; any other day from the picker.
  const quick = [0, 1, 2].map((n) => addDays(today, n)).filter((d) => d <= lastDay);
  return (
    <Sheet title="Add an extra" kicker={`For ${first}`} sub="More of their tiffins on one day. Billed on its own." onClose={onClose}>
      <div className="stack">
        <span className="label">Day</span>
        <Chips label="Day">
          {quick.map((d, i) => <Chip key={d} on={!picking && day === d} onClick={() => { setDay(d); setPicking(false); }}>{i === 0 ? 'Today' : formatDay(d).split(' ').slice(0, 2).join(' ')}</Chip>)}
          <Chip on={picking || !quick.includes(day)} onClick={() => setPicking(true)}>{picking || quick.includes(day) ? 'Pick a day' : formatDay(day)}</Chip>
        </Chips>
        {picking && <Field label="Day" id="extra-day" type="date" value={day} onChange={setDay} min={today} max={lastDay} error={fields.day} hint={`Up to ${formatDay(lastDay)}, the plan’s last day.`} />}
        {!picking && fields.day && <span className="hint bad">{fields.day}</span>}
      </div>
      {meals.length > 1 && <Seg label="Meal" value={meal} onChange={setMeal} options={meals.map((m) => ({ value: m, label: mealName(m) }))} />}
      <PCard>
        <Row left={<Ic icon="pot" />} t="Tiffins" s={`Same as their ${mealName(meal).toLowerCase()}`}
          right={(
            <span className="p-step">
              <button type="button" aria-label="One tiffin less" disabled={quantity <= 1} onClick={() => setQuantity(Math.max(1, quantity - 1))}>–</button>
              <b>{quantity}</b>
              <button type="button" className="k" aria-label="One tiffin more" disabled={quantity >= 5} onClick={() => setQuantity(Math.min(5, quantity + 1))}>+</button>
            </span>
          )} />
      </PCard>
      <Field label="Price each" id="extra-price" value={each} onChange={(v) => setEach(v.replace(/\D/g, '').slice(0, 5))} prefix="₹" inputMode="numeric" placeholder="0" error={fields.price} hint={quantity > 1 && total > 0 ? `${quantity} × ${formatInr(Number(each))} = ${formatInr(total)}` : 'You set it. Leave empty for no charge.'} />
      <Field label="Note (optional)" id="extra-note" value={note} onChange={setNote} placeholder="Guest at home" maxLength={200} />
      <ErrorText>{save.error && !Object.keys(fields).length ? errorMessage(save.error) : ''}</ErrorText>
      <Btn label={total > 0 ? `Add ${formatInr(total)}` : 'Add extra'} go="check" busy={save.isPending} disabled={save.isPending || !day} onClick={() => { setFields({}); save.mutate(); }} />
    </Sheet>
  );
}
